import { useEffect, useRef, useState } from "react";
import { BookOpen, Download, Minus, Paperclip, Plus, Search, Upload, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import { ContactPicker } from "@/components/ContactPicker";
import { AccountSelect } from "@/components/AccountSelect";
import { SearchSelect } from "@/components/SearchSelect";

const METHODS = ["Tunai", "Transfer Bank", "Cek & Giro", "Kartu Kredit"];
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? d.split("-").reverse().join("-") : "—");

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("biaya");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [paying, setPaying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [checked, setChecked] = useState([]);
  const [message, setMessage] = useState(null);
  const load = () => {
    api("/expenses").then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
    api("/expenses/summary").then(setSummary).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(t);
  }, [message]);
  const filtered = tab === "approval" ? [] : rows.filter((r) => [r.number, r.date, r.category, r.contact, r.tag].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, `${tab}|${search}`);
  const pageIds = pager.pageItems.map((r) => r.id);
  const allChecked = pageIds.length > 0 && pageIds.every((id) => checked.includes(id));
  const toggleAll = () => setChecked(allChecked ? checked.filter((id) => !pageIds.includes(id)) : [...new Set([...checked, ...pageIds])]);
  const onSaved = (record) => { const edited = Boolean(editing); setCreating(false); setEditing(null); setMessage({ text: edited ? `${record.number} berhasil diubah — jurnal ${record.journal_number} dibuat ulang` : `${record.number} berhasil dibuat — jurnal ${record.journal_number} otomatis diposting`, type: "success" }); load(); };
  const onPaid = (record) => { setPaying(null); setSelected(record); setMessage({ text: `${record.number} berhasil dibayar dan berstatus Paid`, type: "success" }); load(); };
  const remove = async () => {
    try {
      await api(`/expenses/${deleting.id}`, { method: "DELETE" });
      setSelected((prev) => (prev?.id === deleting.id ? null : prev));
      setMessage({ text: `${deleting.number} dihapus — jurnal terkait dibatalkan (void)`, type: "success" });
      load();
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  if (creating || editing) return <ExpenseForm initial={editing} onCancel={() => { setCreating(false); setEditing(null); }} onSaved={onSaved} />;
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">BIAYA</p>
          <h1>Pengeluaran</h1>
        </div>
        <button data-testid="expense-create-button" className="primary-btn" onClick={() => setCreating(true)}><Plus size={16} /> Buat Biaya Baru</button>
      </div>
      {message && <div data-testid={message.type === "error" ? "expense-error-message" : "expense-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button data-testid="expense-dismiss-message" onClick={() => setMessage(null)}><X size={14} /></button></div>}
      {summary && (
        <div className="exp-cards" data-testid="expense-summary-cards">
          <div className="exp-card" data-testid="expense-month-card">
            <div className="exp-card-head"><span>Total Biaya Bulan Ini (dalam IDR)</span><i className="exp-badge">{summary.month.count}</i></div>
            <div className="exp-card-body"><small>Total</small><strong>{money(summary.month.total)}</strong></div>
          </div>
          <div className="exp-card" data-testid="expense-30d-card">
            <div className="exp-card-head"><span>Biaya 30 Hari Terakhir (dalam IDR)</span><i className="exp-badge">{summary.last30.count}</i></div>
            <div className="exp-card-body"><small>Total</small><strong>{money(summary.last30.total)}</strong></div>
          </div>
          <div className="exp-card" data-testid="expense-unpaid-card">
            <div className="exp-card-head"><span>Biaya Belum Dibayar (dalam IDR)</span><i className="exp-badge">{summary.unpaid.count}</i></div>
            <div className="exp-card-body"><small>Total</small><strong>{money(summary.unpaid.total)}</strong></div>
          </div>
        </div>
      )}
      <p className="exp-note">Saldo adalah untuk semua jangka waktu, kecuali ada pernyataan lain</p>
      <div className="surface exp-list-wrap">
        <div className="exp-list-head">
          <h2>Daftar Biaya</h2>
          <div className="exp-list-tools">
            <button data-testid="expense-import-button" className="secondary-btn small" onClick={() => setMessage({ text: "Fitur impor biaya dari Excel akan segera hadir", type: "success" })}><Upload size={14} /> Impor</button>
            <div className="workflow-search"><Search size={16} /><input data-testid="expense-search-input" placeholder="Pencarian..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          </div>
        </div>
        <div className="report-tabs exp-tabs" data-testid="expense-tabs">
          <button data-testid="expense-tab-biaya" className={tab === "biaya" ? "active" : ""} onClick={() => setTab("biaya")}>Biaya</button>
          <button data-testid="expense-tab-approval" className={tab === "approval" ? "active" : ""} onClick={() => setTab("approval")}>Membutuhkan Persetujuan <i className="cb-badge orange inline">0</i></button>
        </div>
        <table className="workflow-table" data-testid="expense-table">
          <thead><tr><th className="check-col"><input type="checkbox" data-testid="expense-check-all" checked={allChecked} onChange={toggleAll} /></th><th>TANGGAL</th><th>NOMOR</th><th>KATEGORI</th><th>PENERIMA</th><th>STATUS</th><th className="right">SISA TAGIHAN (DALAM IDR)</th><th className="right">TOTAL (DALAM IDR)</th><th>TAGS</th></tr></thead>
          <tbody>
            {pager.pageItems.map((r) => (
              <tr key={r.id} onClick={() => setSelected(r)} data-testid={`expense-row-${r.id}`}>
                <td className="check-col"><input type="checkbox" data-testid={`expense-check-${r.id}`} checked={checked.includes(r.id)} onChange={() => setChecked((prev) => (prev.includes(r.id) ? prev.filter((x) => x !== r.id) : [...prev, r.id]))} onClick={(e) => e.stopPropagation()} /></td>
                <td>{fmtDate(r.date)}</td>
                <td><span className="cb-account-link">{r.number}</span></td>
                <td>{r.category}</td>
                <td>{r.contact || "—"}</td>
                <td>{r.status === "PAID" ? <span className="status paid" data-testid={`expense-status-${r.id}`}>Paid</span> : <span className="status partial" data-testid={`expense-status-${r.id}`}>Belum Dibayar</span>}</td>
                <td className="right mono">{money(r.remaining)}</td>
                <td className="right mono">{money(r.total)}</td>
                <td>{r.tag ? <span className="exp-tag">{r.tag}</span> : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="expense-empty-state"><BookOpen size={25} /><strong>{tab === "approval" ? "Tidak ada biaya yang membutuhkan persetujuan" : "Belum ada biaya"}</strong><span>{tab === "approval" ? "Semua biaya sudah disetujui." : "Buat biaya pertama Anda dengan tombol Buat Biaya Baru."}</span></div>}
        <Pagination pager={pager} testid="expense-pagination" />
      </div>
      {deleting && (
        <div className="modal-overlay" data-testid="expense-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head">
                <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Biaya?</h2></div>
                <button data-testid="expense-delete-close" className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
              </div>
              <p className="muted delete-warning">{deleting.number} sebesar {money(deleting.total)} akan dihapus. Jurnal terkait akan dibatalkan (void).</p>
              <div className="form-actions">
                <button data-testid="expense-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="expense-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {paying && <ExpensePayModal expense={paying} onClose={() => setPaying(null)} onDone={onPaid} onError={(text) => { setPaying(null); setMessage({ text, type: "error" }); }} />}
      {selected && <ExpenseDrawer expense={selected} onClose={() => setSelected(null)} onPay={() => { setPaying(selected); setSelected(null); }} onEdit={() => { setEditing(selected); setSelected(null); }} onDelete={() => setDeleting(selected)} />}
    </div>
  );
}

function ExpenseDrawer({ expense, onClose, onPay, onEdit, onDelete }) {
  const downloadAttachment = async () => {
    const doc = await api(`/expenses/${expense.id}/attachment`);
    const a = document.createElement("a");
    a.href = `data:${doc.content_type};base64,${doc.data}`;
    a.download = doc.filename;
    a.click();
  };
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer" data-testid="expense-detail-drawer">
        <div className="drawer-head">
          <div><p className="eyebrow">DETAIL BIAYA</p><h2>{expense.number}</h2></div>
          <button data-testid="expense-close-detail" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="detail-status">
          {expense.status === "PAID" ? <span className="status paid">Paid</span> : <span className="status partial">Belum Dibayar</span>}
          <span className="muted">{fmtDate(expense.date)}</span>
        </div>
        <div className="detail-fields">
          <div><small>penerima</small><strong>{expense.contact || "—"}</strong></div>
          <div><small>bayar dari</small><strong>{expense.pay_later && expense.status !== "PAID" ? "Bayar Nanti (Hutang)" : expense.pay_from_name || expense.pay_account_name || "—"}</strong></div>
          <div><small>cara pembayaran</small><strong>{expense.method}</strong></div>
          <div><small>kategori</small><strong>{expense.category}</strong></div>
          <div><small>jurnal</small><strong>{expense.journal_number}</strong></div>
          {expense.tag && <div><small>tag</small><strong>{expense.tag}</strong></div>}
          {expense.billing_address && <div><small>alamat penagihan</small><strong>{expense.billing_address}</strong></div>}
          {expense.memo && <div><small>memo</small><strong>{expense.memo}</strong></div>}
        </div>
        <div className="doc-lines" data-testid="expense-lines">
          <h3>Rincian biaya</h3>
          <table className="doc-line-table">
            <thead><tr><th>AKUN BIAYA</th><th>DESKRIPSI</th><th>PAJAK</th><th className="right">JUMLAH</th></tr></thead>
            <tbody>
              {(expense.lines || []).map((l, i) => (
                <tr key={i}><td>{l.account_name}</td><td>{l.description || "—"}</td><td>{l.tax || "—"}</td><td className="right mono">{money(l.amount)}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="doc-totals">
            <div><span>SubTotal</span><b>{money(expense.subtotal)}</b></div>
            {expense.tax_amount > 0 && <div><span>PPN Masukan</span><b>{money(expense.tax_amount)}</b></div>}
            {expense.discount > 0 && <div><span>Pemotongan</span><b>-{money(expense.discount)}</b></div>}
            <div><span>Total</span><b>{money(expense.total)}</b></div>
            <div className="outstanding"><span>Sisa tagihan</span><b>{money(expense.remaining ?? expense.total - (expense.paid || 0))}</b></div>
          </div>
        </div>
        {expense.has_attachment && (
          <div className="journal-lines">
            <h3>Lampiran</h3>
            <div><span><Paperclip size={13} /> {expense.attachment_name}</span><button data-testid="expense-download-attachment" className="text-btn" onClick={downloadAttachment}><Download size={13} /> Unduh</button></div>
          </div>
        )}
        <div className="drawer-actions">
          {expense.status === "OPEN" && <button data-testid="expense-pay-button" className="primary-btn" onClick={onPay}>Bayar</button>}
          <button data-testid="expense-drawer-edit" className="secondary-btn" onClick={onEdit}>Ubah</button>
          <button data-testid="expense-drawer-delete" className="danger-btn" onClick={onDelete}>Hapus</button>
        </div>
      </div>
    </>
  );
}

function ExpensePayModal({ expense, onClose, onDone, onError }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_code: "", date: today(), method: "Transfer Bank" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("/cash-accounts").then(setAccounts).catch(() => {}); }, []);
  const submit = async () => {
    setSaving(true);
    try { onDone(await api(`/expenses/${expense.id}/pay`, { method: "POST", body: JSON.stringify(form) })); }
    catch (e) { onError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="expense-pay-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">BAYAR BIAYA</p><h2>{expense.number}</h2></div>
            <button data-testid="expense-pay-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <p className="muted delete-warning">Sisa tagihan {money(expense.remaining ?? expense.total)} akan dibayar penuh dari akun kas/bank yang dipilih.</p>
          <div className="form-grid payment-head-grid">
            <label>Bayar dari
              <SearchSelect testid="expense-pay-account" value={form.account_code} onChange={(v) => setForm({ ...form, account_code: v })}
                placeholder="Pilih akun kas/bank..."
                options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}`, sub: money(a.balance) }))} />
            </label>
            <label>Tanggal<input data-testid="expense-pay-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></label>
          </div>
          <div className="form-actions">
            <button data-testid="expense-pay-confirm" className="primary-btn" disabled={!form.account_code || saving} onClick={submit}>{saving ? "Menyimpan..." : `Bayar ${money(expense.remaining ?? expense.total)}`}</button>
            <button data-testid="expense-pay-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const emptyLine = () => ({ account_code: "", description: "", tax: "", amount: "" });

function ExpenseForm({ onCancel, onSaved, initial }) {
  const fileRef = useRef(null);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [head, setHead] = useState(initial ? { pay_from: initial.pay_from || "", pay_later: Boolean(initial.pay_later), contact: initial.contact || "", date: initial.date, method: initial.method || "Cek & Giro", tag: initial.tag || "", billing_address: initial.billing_address || "", memo: initial.memo || "", tax_inclusive: Boolean(initial.tax_inclusive) } : { pay_from: "", pay_later: false, contact: "", date: today(), method: "Cek & Giro", tag: "", billing_address: "", memo: "", tax_inclusive: false });
  const [lines, setLines] = useState(initial ? initial.lines.map((l) => ({ account_code: l.account_code, description: l.description || "", tax: l.tax || "", amount: l.amount })) : [emptyLine(), emptyLine()]);
  const [showDiscount, setShowDiscount] = useState(Boolean(initial?.discount));
  const [discount, setDiscount] = useState(initial?.discount ? String(initial.discount) : "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api("/cash-accounts").then(setCashAccounts).catch(() => {});
    api("/accounts").then(setExpenseAccounts).catch(() => {});
  }, []);
  const set = (k, v) => setHead({ ...head, [k]: v });
  const addAccount = (acc) => setExpenseAccounts((prev) => [...prev, acc].sort((a, b) => a.code.localeCompare(b.code)));
  const setLine = (i, k, v) => setLines(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const filled = lines.filter((l) => l.account_code && Number(l.amount) > 0);
  const r2 = (n) => Math.round(n * 100) / 100;
  const subtotal = filled.reduce((s, l) => {
    const amt = Number(l.amount);
    return s + (l.tax === "PPN 11%" && head.tax_inclusive ? r2(amt / 1.11) : amt);
  }, 0);
  const taxTotal = filled.reduce((s, l) => {
    if (l.tax !== "PPN 11%") return s;
    const amt = Number(l.amount);
    return s + (head.tax_inclusive ? r2(amt - r2(amt / 1.11)) : r2(amt * 0.11));
  }, 0);
  const total = subtotal + taxTotal - (Number(discount) || 0);
  const valid = filled.length > 0 && total > 0 && head.date && (head.pay_later || head.pay_from);
  const pickFile = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError("Ukuran lampiran maksimal 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ filename: f.name, content_type: f.type || "application/octet-stream", data: String(reader.result).split(",")[1] });
    reader.readAsDataURL(f);
  };
  const submit = async () => {
    setSaving(true); setError("");
    try {
      const payload = { ...head, discount: Number(discount) || 0, lines: filled.map((l) => ({ account_code: l.account_code, description: l.description, tax: l.tax, amount: Number(l.amount) })), attachment: file };
      onSaved(await api(initial ? `/expenses/${initial.id}` : "/expenses", { method: initial ? "PUT" : "POST", body: JSON.stringify(payload) }));
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  return (
    <div className="workflow-page" data-testid="expense-form-page">
      <div className="page-heading">
        <div><p className="eyebrow">BIAYA</p><h1>{initial ? `Ubah Biaya ${initial.number}` : "Buat Biaya"}</h1></div>
        <button data-testid="expense-guide-button" className="secondary-btn small"><BookOpen size={14} /> Lihat panduan</button>
      </div>
      {error && <div className="success-message error" data-testid="expense-form-error">{error}<button onClick={() => setError("")}><X size={14} /></button></div>}
      <div className="surface exp-form-wrap">
        <div className="exp-band" data-testid="expense-band">
          <div className="exp-band-left">
            <label className="exp-field">Bayar Dari
              <SearchSelect testid="expense-pay-from-select" value={head.pay_from} disabled={head.pay_later}
                onChange={(v) => set("pay_from", v)} placeholder="Pilih akun kas/bank..."
                options={cashAccounts.map((a) => ({ value: a.code, label: `(${a.code}) - ${a.name}`, sub: "Kas & Bank" }))} />
            </label>
            <label className="exp-check">
              <input type="checkbox" data-testid="expense-pay-later-checkbox" checked={head.pay_later} onChange={(e) => set("pay_later", e.target.checked)} />
              Bayar Nanti
            </label>
          </div>
          <div className="exp-band-total" data-testid="expense-band-total"><span>Total</span> <strong>{money(total > 0 ? total : 0)}</strong></div>
        </div>
        <div className="form-grid exp-head-grid">
          <label>Penerima
            <ContactPicker
              value={head.contact}
              onPick={(c) => setHead((prev) => ({ ...prev, contact: c.name }))}
              addLabel="Penerima biaya baru"
              defaultTypes={["Supplier"]}
              testPrefix="expense-contact"
              placeholder="Pilih penerima..." />
          </label>
          <label>Tgl Transaksi<input data-testid="expense-date-input" type="date" value={head.date} onChange={(e) => set("date", e.target.value)} /></label>
          <label>Cara Pembayaran
            <select data-testid="expense-method-select" value={head.method} onChange={(e) => set("method", e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>No Biaya<input data-testid="expense-number-input" value={initial ? initial.number : "[Auto]"} disabled /></label>
          <label>Tag<input data-testid="expense-tag-input" value={head.tag} onChange={(e) => set("tag", e.target.value)} /></label>
        </div>
        <label className="exp-address">Alamat Penagihan
          <textarea data-testid="expense-billing-address" rows={3} value={head.billing_address} onChange={(e) => set("billing_address", e.target.value)} />
        </label>
        <div className="exp-tax-toggle">
          <span>Harga termasuk pajak</span>
          <button type="button" data-testid="expense-tax-inclusive-toggle" className={`switch ${head.tax_inclusive ? "on" : ""}`} onClick={() => set("tax_inclusive", !head.tax_inclusive)} aria-pressed={head.tax_inclusive}><i /></button>
        </div>
        <table className="journal-line-table exp-line-table" data-testid="expense-line-table">
          <thead><tr><th>AKUN BIAYA</th><th>DESKRIPSI</th><th>PAJAK</th><th className="right">JUMLAH</th><th /></tr></thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>
                  <AccountSelect accounts={expenseAccounts} value={l.account_code} testid={`expense-line-account-${i}`}
                    onChange={(code) => setLine(i, "account_code", code)} onAccountCreated={addAccount} placeholder="Pilih akun" />
                </td>
                <td><input data-testid={`expense-line-description-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                <td>
                  <select data-testid={`expense-line-tax-${i}`} value={l.tax} onChange={(e) => setLine(i, "tax", e.target.value)}>
                    <option value="">Pilih pajak</option>
                    <option value="PPN 11%">PPN 11%</option>
                  </select>
                </td>
                <td><input data-testid={`expense-line-amount-${i}`} className="right" type="number" min="0" placeholder="Rp. 0,00" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} /></td>
                <td>{lines.length > 1 && <button data-testid={`expense-line-remove-${i}`} className="icon-btn exp-remove" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Minus size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button data-testid="expense-add-line-button" className="primary-btn small exp-add-line" onClick={() => setLines([...lines, emptyLine()])}><Plus size={14} /> Tambah Data</button>
        <div className="exp-bottom">
          <div className="exp-bottom-left">
            <label>Memo<textarea data-testid="expense-memo-input" rows={4} value={head.memo} onChange={(e) => set("memo", e.target.value)} /></label>
            <div className="exp-attachment">
              <span className="exp-attachment-label"><Paperclip size={13} /> Lampiran</span>
              <div className="exp-drop" data-testid="expense-attachment-drop" onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}>
                {file ? <span className="exp-file-name"><Paperclip size={13} /> {file.filename} <button className="text-btn" onClick={(e) => { e.stopPropagation(); setFile(null); }}>Hapus</button></span> : <>Tarik file ke sini, atau <b>pilih file</b><small>ukuran maksimal 5 MB/file</small></>}
              </div>
              <input ref={fileRef} data-testid="expense-attachment-input" type="file" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
            </div>
          </div>
          <div className="exp-bottom-right">
            <div className="exp-total-row"><span>SubTotal</span><b data-testid="expense-subtotal">{money(subtotal)}</b></div>
            {taxTotal > 0 && <div className="exp-total-row"><span>PPN Masukan</span><b data-testid="expense-tax-total">{money(taxTotal)}</b></div>}
            <div className="exp-total-row"><span>Total</span><b>{money(subtotal + taxTotal)}</b></div>
            {!showDiscount && <button data-testid="expense-show-discount" className="text-btn exp-discount-link" onClick={() => setShowDiscount(true)}>➜ Masukan Jumlah Pemotongan</button>}
            {showDiscount && (
              <div className="exp-total-row"><span>Pemotongan</span><input data-testid="expense-discount-input" className="right exp-discount-input" type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0" /></div>
            )}
            <div className="exp-total-row grand"><span>Total</span><b data-testid="expense-grand-total">{money(total > 0 ? total : 0)}</b></div>
          </div>
        </div>
        <div className="form-actions">
          <button data-testid="expense-save-button" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Buat Biaya"}</button>
          <button data-testid="expense-cancel-button" className="secondary-btn" onClick={onCancel}>Batal</button>
        </div>
      </div>
    </div>
  );
}
